import type { FormikErrors, FormikTouched } from "formik";
import { useCallback, useMemo } from "react";

import type { FormFormatMessages } from "@skilitics/designer-ui-components";
import { FieldLabel, Form, FormikForm, FormRowField } from "@skilitics/designer-ui-components";
import type { Operator } from "@skilitics/judy";
import { isTernaryOperator } from "@skilitics/judy";
import { useFormatMessage } from "@skilitics/localization";
import type {
  ReferencedValue,
  ValueConfig,
  ValueExpression,
  ValueTombstone,
} from "@skilitics/nautilus-core-designer-data";
import {
  isNodeValueReference,
  isValueTombstone,
  toReferencedValue,
  valueReference,
} from "@skilitics/nautilus-core-designer-data";

import { useTriggerScopeNodeId } from "../../triggers/HandlerDesignerContext.ts";
import { defaultValueForConfig } from "../defaultValueForConfig.ts";
import { isValueOperandValid } from "../expressions/index.ts";
import { useGetValueSubtypeDefinition } from "../useGetValueSubtypeDefinition.ts";
import { useResolveValueSignature } from "../useResolveValueSignature.ts";
import { useResolveValueToConditionValue } from "../useResolveValueToConditionValue.ts";
import { useValueSignature } from "../useValueSignature.ts";
import type { ComparisonViewProps } from "../valueSubtypeDefinition.ts";
import { FieldBindingPicker } from "./BindingPicker.tsx";
import { useValueBindChoices } from "./useValueBindChoices.ts";

/**
 * The expression as the form holds it: `operator` is widened because a select
 * stores `""` for an unmade choice, and the operands stay possibly-unset while
 * the author works. What an unset operand is stored as is settled on the way
 * out — see `seedOperand`.
 */
type ExpressionFormValues = {
  subject?: ReferencedValue | ValueTombstone;
  operator: Operator | "";
  value?: ValueConfig;
  value2?: ValueConfig;
};

const touchedOnArrival: FormikTouched<ExpressionFormValues> = { operator: true, value: true, value2: true };

export type ExpressionFormProps = Omit<ComparisonViewProps, "subtype" | "subtypeSpec"> & {
  expression: ValueExpression;
  onChange: (expression: ValueExpression) => void;
  dataTest?: string;
};

/**
 * Edits a whole ValueExpression. Core owns the subject in every state and the
 * resolved subtype's Comparison View takes the rest, both inside this form's
 * single submit/blur cycle.
 *
 * The subject comes first because nothing else can: it is what says which type
 * and subtype the comparison is over, so until one is picked no comparison is
 * offered. Once picked it is locked — an expression's type never changes — and
 * the only way to a different subject is to drop the expression. Where the
 * subject's node has been removed, its tombstone still carries the signature, so
 * the comparison stays on screen, disabled, while the picker reopens filtered to
 * the bindings that would fit in its place.
 */
export const ExpressionForm = ({
  expression,
  onChange,
  scopeNodeId,
  excludeNodeId,
  bindable,
  disabled = false,
  dataTest = "expression-form",
}: ExpressionFormProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  const triggerScopeNodeId = useTriggerScopeNodeId();
  const getValueSubtypeDefinition = useGetValueSubtypeDefinition();
  const resolveValue = useResolveValueToConditionValue();

  const scope = scopeNodeId ?? triggerScopeNodeId;
  const { subject } = expression;
  const repairing = isValueTombstone(subject);

  const signature = useValueSignature(subject);
  // Seeding reads a subject that has only just been submitted, so it resolves
  // imperatively rather than through the hook above.
  const resolveValueSignature = useResolveValueSignature();
  const definition = signature && getValueSubtypeDefinition(signature);

  // only while repairing: the replacement has to fit what the expression is already over
  const replacementRequirement = useMemo(
    () => (repairing && signature?.subtype ? definition?.require?.(signature.subtype) : undefined),
    [definition, repairing, signature],
  );

  const choices = useValueBindChoices({
    type: repairing ? signature?.type : undefined,
    subtype: replacementRequirement,
    scopeNodeId: scope,
    excludeNodeId,
  });

  const initialValues = useMemo(
    (): ExpressionFormValues => ({
      subject: expression.subject,
      operator: expression.operator ?? "",
      value: expression.value,
      value2: expression.value2,
    }),
    [expression],
  );

  /**
   * The literal an operand the author has not filled in is stored as: the
   * subject's own subtype default, else its base type's — the same fallback
   * unbinding lands on.
   *
   * Seeded rather than left unset because the controls a Comparison View puts up
   * have no empty state to offer: a number field shows the bottom of its range
   * whether or not anything is stored, so an absent operand reads as a real value
   * to the author while every reader of the config sees nothing there. Read off
   * the submitted subject, not the stored one, so the operand arrives with the
   * subject that decides it.
   */
  const seedOperand = useCallback(
    (submitted: ReferencedValue | ValueTombstone) => {
      const submittedSignature = resolveValueSignature(submitted);
      if (!submittedSignature) return undefined;

      // The declared spec alone: a derived spec says how a value reads, not what
      // is stored under it.
      const { type, subtype, subtypeSpec } = submittedSignature;
      const submittedDefinition = getValueSubtypeDefinition(submittedSignature);
      return submittedDefinition.defaultValue?.(subtype, subtypeSpec) ?? defaultValueForConfig(type);
    },
    [getValueSubtypeDefinition, resolveValueSignature],
  );

  const handleSubmit = useCallback(
    ({ subject: submitted, operator, value, value2 }: ExpressionFormValues) => {
      const seed = submitted && seedOperand(submitted);
      const firstOperand = value ?? seed;
      // the second operand only exists where the operator takes one
      const secondOperand = operator && isTernaryOperator(operator) ? (value2 ?? seed) : value2;

      onChange({
        ...(submitted && { subject: lockSubject(submitted) }),
        ...(operator && { operator }),
        ...(firstOperand !== undefined && { value: firstOperand }),
        ...(secondOperand !== undefined && { value2: secondOperand }),
      });
    },
    [onChange, seedOperand],
  );

  /**
   * A field is in error when what it holds cannot be used — never for being
   * unset. An expression is authored a part at a time and is stored as it goes,
   * incomplete and all; whether it says enough to build is judged over the whole,
   * not by a field. The distinction is load-bearing rather than cosmetic: Formik
   * withholds a submit while any error stands, so erroring on an empty operand
   * would refuse to store the subject the author just picked.
   */
  const validate = useCallback(
    ({ operator, value, value2 }: ExpressionFormValues): FormikErrors<ExpressionFormValues> => {
      if (!signature || !definition) return {};
      const context = { signature, definition, resolveValue };

      const operandError = (operand: ValueConfig | undefined) =>
        operand === undefined || isValueOperandValid(operand, context) ? undefined : formatMessage("operandInvalid");

      const operatorError =
        operator && !definition.operators.includes(operator) ? formatMessage("operatorNotOffered") : undefined;
      const valueError = operandError(value);
      const value2Error = operator && isTernaryOperator(operator) ? operandError(value2) : undefined;

      return {
        ...(operatorError && { operator: operatorError }),
        ...(valueError && { value: valueError }),
        ...(value2Error && { value2: value2Error }),
      };
    },
    [definition, formatMessage, resolveValue, signature],
  );

  const ComparisonView = definition?.views.comparison;

  return (
    <FormikForm<ExpressionFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      validateOnMount
      // Errors here are about what a field holds, never about it being unset (see
      // `validate`), so there is nothing for `touched` to spare the author — and a
      // stored value that arrives unusable is one they never touch.
      initialTouched={touchedOnArrival}
      submitOnBlur
      explicitLabels
      formatStrings={formatStrings}
      dataTest={dataTest}
    >
      <Form $labelAlignment="inline">
        <FormRowField<ExpressionFormValues> name="subject">
          <FieldLabel />
          <FieldBindingPicker
            choices={choices}
            forNodeId={excludeNodeId}
            // locked: the expression is over this value's type, so only a tombstone reopens the pick
            disabled={disabled || (!!subject && !repairing)}
            placeholder={formatMessage("subjectPlaceholder")}
          />
        </FormRowField>

        {ComparisonView && signature && (
          <ComparisonView
            subtype={signature.subtype}
            subtypeSpec={signature.subtypeSpec}
            scopeNodeId={scope}
            excludeNodeId={excludeNodeId}
            bindable={bindable}
            disabled={disabled || repairing}
          />
        )}
      </Form>
    </FormikForm>
  );
};

/**
 * The subject is its expression's type authority, so it is stored as a Locked
 * Reference: removing the node it points at leaves a Value Tombstone carrying the
 * signature forward rather than clearing the binding. Stamped on the way out, so
 * however a subject was picked it is stored the same way.
 *
 * An external value takes no lock — the stored shape has none for one, and the
 * environment never stops offering a value it advertises.
 */
const lockSubject = (subject: ReferencedValue | ValueTombstone) => {
  if (isValueTombstone(subject)) return subject;
  const reference = valueReference(subject);
  return isNodeValueReference(reference) ? toReferencedValue({ ...reference, locked: true }) : subject;
};

const formatStrings = {
  subjectLabel: {
    en: "Subject",
  },
  subjectPlaceholder: {
    en: "Select a value",
  },
  operatorLabel: {
    en: "Operator",
  },
  valueLabel: {
    en: "Value",
  },
  value2Label: {
    en: "To",
  },
  operatorNotOffered: {
    en: "This comparison is no longer offered for this kind of value. Pick another.",
  },
  operandInvalid: {
    en: "This value can't be compared with the subject. Pick another, or type one in.",
  },
} satisfies FormFormatMessages<ExpressionFormValues>;
